import classNames from "classnames";
import { forwardRef } from "react";

interface PiBrowseTileProps {
  icon: React.ReactNode;
  label: string;
  /** Target for the search field's aria-activedescendant. */
  optionId?: string;
  isSelected: boolean;
  onSelect: () => void;
  onConfirm: () => void;
}

/**
 * Selectable card for the browse dialogs' tile view. The selection is a tinted
 * card with the standard 8px radius — never a left accent stripe — and lives
 * here so the treatment cannot drift between dialogs.
 *
 * Single click selects; confirming is the footer button or Enter. The double
 * click stays wired only as a muscle-memory shortcut from the old dialogs.
 */
const PiBrowseTile = forwardRef<HTMLButtonElement, PiBrowseTileProps>(
  function PiBrowseTile(props, ref) {
    const { icon, label, optionId, isSelected, onSelect, onConfirm } = props;

    return (
      <button
        ref={ref}
        id={optionId}
        type="button"
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        onClick={onSelect}
        onDoubleClick={onConfirm}
        className={classNames(
          "h-[96px] rounded-[8px] border flex flex-col items-center justify-center gap-[10px] px-[8px] transition-colors",
          isSelected
            ? "border-creator-primary bg-creator-primary-washed"
            : "border-creator-border hover:bg-creator-childActive"
        )}
      >
        <span className="text-[22px] leading-none text-creator-text-sub flex">
          {icon}
        </span>
        <span className="text-[12px] leading-[16px] text-creator-text text-center line-clamp-2">
          {label}
        </span>
      </button>
    );
  }
);

export default PiBrowseTile;
